/**
 * Utilities for detecting and parsing DMS (Degrees-Minutes-Seconds) coordinate format.
 *
 * Supported formats:
 *   50°23'59.93"N 10°13'07.99"E  — Google Earth style (full pair in one string)
 *   50° 23' 59.93" N              — with spaces
 *   50°23'N                       — degrees + minutes only
 *   50.3997°N                     — decimal degrees with symbol
 *   50°23.998'N                   — decimal minutes
 *
 * Degree symbols:  °  º  ˚
 * Minute symbols:  '  ′  ʹ  '  (U+2019)
 * Second symbols:  "  ″  "  (U+201D)
 * Directions:      N S E W  (case-insensitive)
 */

const SOUTH_WEST = new Set(['s', 'w']);

// Full-string match for a single DMS value (anchored with ^ and $)
const DMS_SINGLE_REGEX =
    /^\s*(\d+(?:[.,]\d+)?)\s*[°º˚]\s*(?:(\d+(?:[.,]\d+)?)\s*['′ʹ\u2019]\s*(?:(\d+(?:[.,]\d+)?)\s*["″\u201d]\s*)?)?\s*([NSEWnsew])\s*$/;

// Un-anchored, global — used with matchAll to find all DMS tokens in a string
const DMS_PAIR_REGEX =
    /(\d+(?:[.,]\d+)?)\s*[°º˚]\s*(?:(\d+(?:[.,]\d+)?)\s*['′ʹ\u2019]\s*(?:(\d+(?:[.,]\d+)?)\s*["″\u201d]\s*)?)?\s*([NSEWnsew])/gi;

function toDecimal(deg, min, sec, dir) {
    const d = parseFloat(deg.replace(',', '.'));
    const m = min ? parseFloat(min.replace(',', '.')) : 0;
    const s = sec ? parseFloat(sec.replace(',', '.')) : 0;

    if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(s)) {
        return null;
    }

    const decimal = d + m / 60 + s / 3600;
    return SOUTH_WEST.has(dir.toLowerCase()) ? -decimal : decimal;
}

/**
 * Parse a single DMS string into decimal degrees.
 * Returns the decimal value or null if the input is not valid DMS.
 *
 * @param {string} input — e.g. `50°23'59.93"N`
 * @returns {number|null}
 */
export function parseSingleDms(input) {
    const match = String(input).match(DMS_SINGLE_REGEX);
    if (!match) return null;
    return toDecimal(match[1], match[2], match[3], match[4]);
}

/**
 * Parse a string containing two DMS coordinates (latitude + longitude) into decimal degrees.
 * The two coordinates may be in a single string or the function may be called with two
 * separate strings, each containing one DMS value.
 *
 * @param {string} inputA — may contain both coords or just the first
 * @param {string} [inputB] — optionally the second coordinate
 * @returns {{ lat: number, lon: number }|null}
 */
export function parseFullDmsPair(inputA, inputB) {
    const combined = inputB
        ? `${String(inputA)} ${String(inputB)}`
        : String(inputA);

    const tokens = [...combined.matchAll(DMS_PAIR_REGEX)];
    if (tokens.length !== 2) return null;

    const [matchA, matchB] = tokens;
    const decA = toDecimal(matchA[1], matchA[2], matchA[3], matchA[4]);
    const decB = toDecimal(matchB[1], matchB[2], matchB[3], matchB[4]);

    if (decA === null || decB === null) return null;

    const dirA = matchA[4].toUpperCase();
    const dirB = matchB[4].toUpperCase();

    const isLatFirst = (dirA === 'N' || dirA === 'S') && (dirB === 'E' || dirB === 'W');
    const isLonFirst = (dirA === 'E' || dirA === 'W') && (dirB === 'N' || dirB === 'S');

    if (!isLatFirst && !isLonFirst) return null;

    return isLatFirst
        ? { lat: decA, lon: decB }
        : { lat: decB, lon: decA };
}

// Separators between the two values in a plain decimal pair.
// Split only on whitespace and semicolons first to avoid breaking European decimals (50,3997).
// Commas are handled separately as a fallback separator.
const WHITESPACE_SEMI_REGEX = /[\s;]+/;
const COMMA_SEPARATOR_REGEX = /,/;

/**
 * Parse a single input string that may contain two coordinates in any supported format:
 *   - DMS pair: "50°23'59.93\"N 10°13'07.99\"E"
 *   - Two decimal values: "50.3997 10.2189" / "50.3997, 10.2189" / "50,3997 10,2189"
 *
 * Returns { lat, lon } or null if the input cannot be parsed as a valid pair.
 *
 * @param {string} raw
 * @returns {{ lat: number, lon: number }|null}
 */
export function parseSingleFieldInput(raw) {
    if (!raw || !String(raw).trim()) return null;

    // 1. Try DMS pair
    const dmsPair = parseFullDmsPair(raw);
    if (dmsPair !== null) return dmsPair;

    // 2. Split on whitespace / semicolons, then strip trailing commas (handles "50.3997, 10.2189")
    const wsParts = String(raw)
        .trim()
        .split(WHITESPACE_SEMI_REGEX)
        .map((p) => p.replace(/,+$/, ''))  // strip trailing comma
        .filter(Boolean);

    if (wsParts.length === 2) {
        const a = parseFloat(wsParts[0].replace(',', '.'));
        const b = parseFloat(wsParts[1].replace(',', '.'));
        if (Number.isFinite(a) && Number.isFinite(b)) return { lat: a, lon: b };
    }

    // 3. Fallback: try comma as the only separator (e.g. "50.3997,10.2189" — no space)
    const commaParts = String(raw).trim().split(COMMA_SEPARATOR_REGEX).filter(Boolean);
    if (commaParts.length === 2) {
        const a = parseFloat(commaParts[0]);
        const b = parseFloat(commaParts[1]);
        if (Number.isFinite(a) && Number.isFinite(b)) return { lat: a, lon: b };
    }

    return null;
}
